import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { AuthLayout } from "~/components/auth/auth-layout";
import { NewPasswordForm } from "~/components/auth/new-password-form";
import { Alert } from "~/components/ui/feedback";
import { AuthError, authApi } from "~/lib/auth";

export const Route = createFileRoute("/_auth/reset-password")({
  validateSearch: z.object({ token: z.string().optional().catch(undefined) }),
  component: ResetPasswordPage,
});

export function invalidLinkMessage(error: unknown): string | null {
  return error instanceof AuthError && error.code === "INVALID_TOKEN"
    ? "This link has expired or has already been used. Request a new one."
    : null;
}

function ResetPasswordPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();

  const footer = (
    <Link to="/forgot-password" className="font-medium text-primary hover:underline">
      Request a new link
    </Link>
  );

  if (!token) {
    return (
      <AuthLayout title="Reset your password" footer={footer}>
        <Alert tone="error" title="This reset link is incomplete.">
          Open the link from your email again, or request a new one.
        </Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Choose a new password"
      description="You'll be signed out of every device once it's changed."
      footer={footer}
    >
      <NewPasswordForm
        submitLabel="Update password"
        onSubmit={async (password) => {
          try {
            await authApi.resetPassword(token, password);
          } catch (error) {
            const expired = invalidLinkMessage(error);
            if (expired) throw new AuthError(400, expired);
            throw error;
          }
          await navigate({ to: "/login", search: { reason: "reset" } });
        }}
      />
    </AuthLayout>
  );
}
